import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ShieldCheck,
  Loader2,
  CheckCircle2,
  FileText,
  Mail,
  BadgeCheck,
  PenLine,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "review", label: "Review Contract", icon: FileText },
  { key: "email", label: "Verify Email", icon: Mail },
  { key: "identity", label: "Verify Identity", icon: BadgeCheck },
  { key: "sign", label: "Sign", icon: PenLine },
];

function StepPill({ step, active, done }) {
  const Icon = step.icon;
  return (
    <div
      className={`flex-1 flex items-center gap-2 px-3 py-2 border ${
        done ? "border-emerald-400/60 text-emerald-300"
             : active ? "border-[#C9A227] text-white"
             : "border-white/10 text-gray-500"
      }`}
      data-testid={`step-pill-${step.key}`}
    >
      <Icon className="w-4 h-4" strokeWidth={1.5} />
      <span className="text-[10px] uppercase tracking-widest font-bold">{step.label}</span>
    </div>
  );
}

function ReviewStep({ contract, onAck, busy }) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="space-y-4" data-testid="step-review">
      <div className="card-sharp p-6 max-h-96 overflow-y-auto" data-testid="sign-contract-body">
        <pre className="contract-body whitespace-pre-wrap text-sm leading-relaxed text-gray-200">
          {contract.body}
        </pre>
      </div>
      <label className="flex items-start gap-3 text-sm text-gray-300">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="mt-1 accent-[#C9A227]"
          data-testid="review-ack-checkbox"
        />
        <span>
          I have read the full contract above in its entirety and I understand its terms. I will
          not be able to sign until I complete email and identity verification.
        </span>
      </label>
      <button
        onClick={onAck}
        disabled={!checked || busy}
        className="btn-gold w-full inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="review-continue-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
        I have reviewed — Continue
      </button>
    </div>
  );
}

function EmailStep({ token, onVerified }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [stage, setStage] = useState("request"); // request → confirm
  const [busy, setBusy] = useState(false);
  const [devCode, setDevCode] = useState("");

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.signRequestEmailCode(token, email);
      setStage("confirm");
      if (r.dev_code) {
        setDevCode(r.dev_code);
        toast.message("Dev-mode verification code", {
          description: `Enter ${r.dev_code} to proceed. Email dispatch not yet configured.`,
        });
      } else {
        toast.success("Check your inbox", { description: `Code sent to ${email}` });
      }
    } catch (err) {
      toast.error("Could not send code", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.signVerifyEmailCode(token, code);
      toast.success("Email verified");
      onVerified();
    } catch (err) {
      toast.error("Code rejected", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const inputBase =
    "w-full bg-black/50 border border-white/10 px-4 py-3 text-sm focus:border-[#C9A227] focus:outline-none focus:ring-1 focus:ring-[#C9A227] rounded-none";

  return (
    <form onSubmit={stage === "request" ? requestCode : confirmCode} className="space-y-4" data-testid="step-email">
      {stage === "request" ? (
        <>
          <div className="label-tag">Verification Email</div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="you@company.com"
            className={inputBase}
            data-testid="email-input"
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-gold w-full inline-flex items-center justify-center gap-2"
            data-testid="email-request-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send 6-digit code
          </button>
        </>
      ) : (
        <>
          <div className="label-tag">Enter the 6-digit code sent to {email}</div>
          {devCode ? (
            <div className="text-[11px] text-amber-400 border border-amber-400/40 px-3 py-2">
              Dev mode — code: <span className="font-mono">{devCode}</span>
            </div>
          ) : null}
          <input
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            required
            placeholder="000000"
            className={`${inputBase} tracking-[0.5em] text-center font-mono`}
            data-testid="email-code-input"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setStage("request")}
              className="text-xs label-tag hover:text-white"
              data-testid="email-change-btn"
            >
              ← Change email
            </button>
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="btn-gold flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="email-verify-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Verify email
            </button>
          </div>
        </>
      )}
    </form>
  );
}

function IdentityStep({ token, defaultName, onVerified }) {
  const [fullName, setFullName] = useState(defaultName || "");
  const [docType, setDocType] = useState("passport");
  const [docNumber, setDocNumber] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const inputBase =
    "w-full bg-black/50 border border-white/10 px-4 py-3 text-sm focus:border-[#C9A227] focus:outline-none focus:ring-1 focus:ring-[#C9A227] rounded-none";

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let b64 = null;
      let mime = null;
      if (docFile) {
        if (docFile.size > 2 * 1024 * 1024) {
          toast.error("File too large", { description: "Please upload an image under 2 MB." });
          setBusy(false);
          return;
        }
        b64 = await readFileAsBase64(docFile);
        mime = docFile.type || null;
      }
      await api.signVerifyIdentity(token, {
        full_legal_name: fullName,
        document_type: docType,
        document_number: docNumber,
        document_file_base64: b64,
        document_mime_type: mime,
      });
      toast.success("Identity verified");
      onVerified(fullName);
    } catch (err) {
      toast.error("Identity rejected", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="step-identity">
      <div>
        <div className="label-tag mb-2">Full Legal Name (must match your ID)</div>
        <input
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          required
          className={inputBase}
          placeholder="As written on your passport or license"
          data-testid="identity-name-input"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="label-tag mb-2">Document Type</div>
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className={inputBase}
            data-testid="identity-doc-type"
          >
            <option value="passport">Passport</option>
            <option value="drivers_license">Driver&apos;s License</option>
            <option value="state_id">State ID</option>
            <option value="national_id">National ID</option>
            <option value="ssn">SSN (last 4)</option>
          </select>
        </div>
        <div>
          <div className="label-tag mb-2">Document Number</div>
          <input
            value={docNumber}
            onChange={(e) => setDocNumber(e.target.value)}
            required
            className={inputBase}
            placeholder={docType === "ssn" ? "Last 4 of SSN" : "Document number"}
            data-testid="identity-doc-number"
          />
        </div>
      </div>
      <div>
        <div className="label-tag mb-2">Upload Government ID (optional, image &lt; 2MB)</div>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setDocFile(e.target.files?.[0] || null)}
          className="text-xs text-gray-400 file:mr-3 file:px-3 file:py-1.5 file:bg-[#C9A227]/20 file:text-[#C9A227] file:border-0 file:text-[10px] file:uppercase file:tracking-widest file:font-bold"
          data-testid="identity-doc-file"
        />
      </div>
      <p className="text-[10px] text-gray-500 leading-relaxed">
        We store only the document type, the last 4 characters of its number, and a one-way hash of
        the full value for audit purposes. Your IP address and timestamp are logged at every step.
      </p>
      <button
        type="submit"
        disabled={busy}
        className="btn-gold w-full inline-flex items-center justify-center gap-2"
        data-testid="identity-submit-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}
        Verify Identity
      </button>
    </form>
  );
}

function SignStep({ token, defaultName, onSigned }) {
  const [name, setName] = useState(defaultName || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const updated = await api.signWithToken(token, name);
      toast.success("Sealed", { description: "Your signature is on the Imperial record." });
      onSigned(updated);
    } catch (err) {
      toast.error("Signing failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="step-sign">
      <div className="label-tag">Final Signature — must match your verified identity name</div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Your full legal name"
        required
        className="w-full bg-black/50 border border-white/10 px-4 py-3 text-sm focus:border-[#C9A227] focus:outline-none focus:ring-1 focus:ring-[#C9A227] rounded-none"
        data-testid="sign-name-input"
      />
      <button
        type="submit"
        disabled={busy}
        className="btn-gold w-full inline-flex items-center justify-center gap-2"
        data-testid="sign-submit-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        Seal With My Signature
      </button>
      <p className="text-[10px] text-gray-500 text-center">
        By signing, you affirm your binding intent under the EIGOO Sovereign Standard. Your IP
        address and the signing timestamp are logged.
      </p>
    </form>
  );
}

export default function Sign() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [verification, setVerification] = useState(null);
  const [used, setUsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [identityName, setIdentityName] = useState("");

  const refresh = async () => {
    const d = await api.signLookup(token);
    setContract(d.contract);
    setUsed(d.used);
    setVerification(d.verification || {});
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refresh()
      .catch((err) =>
        toast.error("Invalid signing link", {
          description: err.response?.data?.detail || err.message,
        }),
      )
      .finally(() => setLoading(false));
  }, [token]);

  const handleReviewAck = async () => {
    setBusy(true);
    try {
      await api.signReviewAck(token);
      await refresh();
    } catch (err) {
      toast.error("Could not acknowledge", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-gray-500"
        data-testid="sign-loading"
      >
        <Loader2 className="w-6 h-6 animate-spin mr-3" /> Validating sigil…
      </div>
    );
  }

  if (!contract) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-gray-400"
        data-testid="sign-invalid"
      >
        Invalid or expired signing link.
      </div>
    );
  }

  const signed = contract.status === "signed";
  const currentStepKey = signed
    ? "done"
    : !verification?.reviewed
    ? "review"
    : !verification?.email_verified
    ? "email"
    : !verification?.identity_verified
    ? "identity"
    : "sign";

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white py-12 px-6" data-testid="sign-page">
      <div className="max-w-3xl mx-auto space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 border border-[#C9A227]/40 px-3 py-1.5">
            <ShieldCheck className="w-4 h-4 gold-text" strokeWidth={1.5} />
            <span className="text-[10px] label-tag gold-text">Digital Handshake</span>
          </div>
          <h1 className="text-4xl font-black tracking-tighter mt-4">Sovereign Legal</h1>
          <p className="text-gray-400 mt-2 text-sm">
            Review the full contract, verify your email and government ID, then seal with your
            signature.
          </p>
        </div>

        <div className="card-sharp p-5 space-y-3">
          <div className="label-tag">{contract.template_name}</div>
          <h2 className="text-2xl font-bold">{contract.counterparty}</h2>
          <div className="font-mono text-xs gold-text">{contract.contract_code}</div>
          <div className="grid grid-cols-2 gap-4 pt-2 text-sm">
            <div>
              <div className="label-tag">Signatory (EIGOO)</div>
              <div className="font-bold mt-1">{contract.admiral_name || "Dontae Kirk"}</div>
              <div className="text-[10px] text-gray-500">Director, EIGOO Inc.</div>
            </div>
            <div>
              <div className="label-tag">Reference</div>
              <div className="font-mono mt-1">{contract.vessel_id || "—"}</div>
            </div>
          </div>
        </div>

        {!signed && !used && (
          <div className="flex gap-2 flex-wrap sm:flex-nowrap">
            {STEPS.map((s) => (
              <StepPill
                key={s.key}
                step={s}
                active={s.key === currentStepKey}
                done={
                  (s.key === "review" && verification?.reviewed) ||
                  (s.key === "email" && verification?.email_verified) ||
                  (s.key === "identity" && verification?.identity_verified) ||
                  (s.key === "sign" && signed)
                }
              />
            ))}
          </div>
        )}

        {signed ? (
          <div className="card-sharp p-8 text-center" data-testid="sign-complete">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
            <h3 className="text-2xl font-black tracking-tighter mt-4">The Seal is Set.</h3>
            <p className="text-gray-400 mt-2">
              Signed by <span className="text-white font-bold">{contract.signed_by}</span> on{" "}
              {contract.signed_at
                ? new Date(contract.signed_at).toLocaleString()
                : "—"}
            </p>
          </div>
        ) : used ? (
          <div className="card-sharp p-8 text-center text-gray-400" data-testid="sign-used">
            This signing link has already been used.
          </div>
        ) : currentStepKey === "review" ? (
          <ReviewStep contract={contract} onAck={handleReviewAck} busy={busy} />
        ) : currentStepKey === "email" ? (
          <EmailStep token={token} onVerified={refresh} />
        ) : currentStepKey === "identity" ? (
          <IdentityStep
            token={token}
            defaultName={identityName}
            onVerified={async (name) => {
              setIdentityName(name);
              await refresh();
            }}
          />
        ) : (
          <SignStep
            token={token}
            defaultName={identityName}
            onSigned={async () => {
              await refresh();
            }}
          />
        )}
      </div>
    </div>
  );
}
